import {AfterViewInit, Component, DoCheck, EventEmitter, Input, KeyValueDiffer, KeyValueDiffers, OnInit, Output, ViewChild} from '@angular/core';
import {HttpParams} from "@angular/common/http";
import {Word} from "../../../dtos/vocab/entry/word";
import {Country} from "../../../shared/Country";
import {FormOptionsLists} from "../../../shared/FormOptionsLists";
import {NotificationService} from "../../../services/common/notification.service";
import {Router} from "@angular/router";
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {animate, state, style, transition, trigger} from "@angular/animations";
import {SysGrammarService} from "../../../services/grammar/sys-grammar.service";
import {WordService} from "../../../services/vocab/word.service";
import {FuncGrammarService} from "../../../services/grammar/func-grammar.service";
import {MatDialog} from "@angular/material/dialog";
import {SystemicGrammarEntryDto} from "../../../dtos/grammar/sys/systemic-grammar-entry-dto";
import {FuncGrammarEntryDto} from "../../../dtos/grammar/func/func-grammar-entry-dto";
import {ConfirmationDialogComponent} from "../confirmation-dialog/confirmation-dialog.component";
import {NewWordComponent} from "../../vocabulary/create/new-word/new-word.component";
import {NewGrammarEntryComponent} from "../../grammar/new-grammar-entry/new-grammar-entry.component";
import {merge, Subject} from "rxjs";
import {startWith, switchMap} from "rxjs/operators";
import {SearchTriggerObject} from "../../../shared/SearchTrigger";
import {TextTypeService} from "../../../services/text/text-type.service";
import {SharedService} from "../../../services/common/shared.service";
import {NewTextTypeComponent} from "../../text-type/new-text-type/new-text-type.component";
import {VerbalActionEntryService} from "../../../services/verbal-action/verbal-action-entry.service";
import {NewVerbalActionComponent} from "../../verbal-action/create/new-verbal-action/new-verbal-action.component";
import {GlobalCanDoStatementService} from "../../../services/can-do-statement/global-can-do-statement.service";
import {DetailedCanDoStatementService} from "../../../services/can-do-statement/detailed-can-do-statement.service";
import {NewCanDoStatementComponent} from "../../can-do-statement/create/new-can-do-statement/new-can-do-statement.component";
import {FolderItemType} from "../../../dtos/folder/folder";
import {FolderService} from "../../../services/folder/folder.service";
import {CurrentUserService} from "../../../services/user/current-user.service";
import {FolderActionsService} from "../../../services/folder/folder-actions.service";
import {folderUsageText} from "../../../shared/folder/folder-utils";
import {AuthService} from "../../../services/auth/auth.service";
import {catchError, map} from "rxjs/operators";
import {Observable, of} from "rxjs";

// TODO change AUSTRIA to Österreich and so on
@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
  animations: [
    trigger('detailExpand', [
      state('done', style({height: '0px', minHeight: '0'})),
      state('expanded', style({height: '*'})),
      transition('expanded <=> done', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  standalone: false
})
export class TableComponent implements OnInit, AfterViewInit, DoCheck {

  @Input() displayedColumns: string[];
  @Input() isAuthorized: boolean;
  @Input() service: WordService | SysGrammarService | FuncGrammarService | TextTypeService | VerbalActionEntryService
    | GlobalCanDoStatementService | DetailedCanDoStatementService;
  @Input() selectedCategory: number;
  @Input() displayEditingTools: boolean;
  @Input() searchTrigger: SearchTriggerObject;

  /** shows the checkboxes to add entries to a Sammelmappe, turning it off clears the selection */
  @Input() set selectable(selectable: boolean) {
    this._selectable = selectable;
    if (!selectable) {
      this.selectedIds.clear();
    }
  }

  get selectable(): boolean {
    return this._selectable;
  }

  private _selectable = false;

  @Output() setTab = new EventEmitter<number>();
  @Output() getRoots = new EventEmitter<void>();

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  data: any = [];
  resultsLength: number;

  expandedElement: Word | null;

  // filter options
  formOptionsLists = new FormOptionsLists();


  details: [string, any][];

  isLoadingResults = true;

  // entries selected to be added to a Sammelmappe, kept across pages and searches
  selectedIds = new Set<number>();
  canUseFolders = false;
  private columnsCache: { key: string, columns: string[] };

  // every search trigger (filter change, post-edit/delete refresh) is pushed here so it
  // shares the switchMap pipeline in ngAfterViewInit with sort/page changes, instead of
  // firing its own uncancelled request that could resolve out of order
  private searchSubject = new Subject<void>();

  private differ: KeyValueDiffer<string, any>;

  constructor(private notificationService: NotificationService, private router: Router, private dialog: MatDialog,
              private differs: KeyValueDiffers, protected sharedService: SharedService,
              private folderService: FolderService, private folderActions: FolderActionsService,
              private currentUserService: CurrentUserService, private authService: AuthService) {
    this.differ = this.differs.find({}).create();
  }

  ngOnInit(): void {
    this.canUseFolders = this.currentUserService.canUseFolders();
  }

  /**
   * the displayed columns plus the selection column for users who can use Sammelmappen
   */
  get columns(): string[] {
    const displayed = this.displayedColumns ?? [];
    const withSelect = this.selectable && this.canUseFolders && this.folderItemType !== null;
    const key = withSelect + ':' + displayed.join();
    if (this.columnsCache?.key !== key) {
      this.columnsCache = {key, columns: withSelect ? ['select', ...displayed] : [...displayed]};
    }
    return this.columnsCache.columns;
  }

  get folderItemType(): FolderItemType | null {
    if (this.service instanceof WordService) {
      return 'VOCAB_ENTRY';
    } else if (this.service instanceof FuncGrammarService) {
      return 'FUNCTIONAL_GRAMMAR_ENTRY';
    } else if (this.service instanceof SysGrammarService) {
      return 'SYSTEMIC_GRAMMAR_ENTRY';
    } else if (this.service instanceof GlobalCanDoStatementService) {
      return 'GLOBAL_CDS_ENTRY';
    } else if (this.service instanceof DetailedCanDoStatementService) {
      return 'DETAILED_CDS_ENTRY';
    } else if (this.service instanceof VerbalActionEntryService) {
      return 'VERBAL_ACTION_ENTRY';
    } else if (this.service instanceof TextTypeService) {
      return 'TEXT_TYPE';
    }
    return null;
  }

  isPageSelected(): boolean {
    return this.data.length > 0 && this.data.every(e => this.selectedIds.has(e.id));
  }

  isPagePartlySelected(): boolean {
    return this.data.some(e => this.selectedIds.has(e.id)) && !this.isPageSelected();
  }

  togglePage(checked: boolean): void {
    this.data.forEach(e => checked ? this.selectedIds.add(e.id) : this.selectedIds.delete(e.id));
  }

  toggleSelection(id: number, checked: boolean): void {
    if (checked) {
      this.selectedIds.add(id);
    } else {
      this.selectedIds.delete(id);
    }
  }

  addSelectionToFolder(): void {
    const type = this.folderItemType;
    const items = [...this.selectedIds].map(id => ({type, id}));
    this.folderActions.addToFolder(items).subscribe(() => this.selectedIds.clear());
  }

  /**
   * admin only: how many Sammelmappen contain the entry, as sentence for the delete dialog
   */
  private usageText(id: number): Observable<string> {
    const type = this.folderItemType;
    if (!type || this.authService.getUserRole() !== 'ROLE_ADMIN') {
      return of('');
    }
    return this.folderService.getUsage(type, id).pipe(
      map(usage => usage.folderCount > 0
        ? ' ' + folderUsageText(usage) + ' Der Eintrag wird daraus entfernt und die Nutzer werden benachrichtigt.'
        : ''),
      catchError(() => of(''))
    );
  }

  ngDoCheck(): void {
    const changes = this.differ.diff(this.searchTrigger);
    if (changes) {
      let changed = false;
      changes.forEachChangedItem(change => {
        if (change.previousValue !== change.currentValue) {
          changed = true;
        }
        if (changed) {
          this.searchSubject.next();
        }
      });
    }
  }


  ngAfterViewInit(): void {
    merge(this.sort.sortChange, this.paginator.page, this.searchSubject)
      .pipe(
        startWith({}),
        switchMap(() => {
          this.isLoadingResults = true;
          // drop the previous results immediately so the table doesn't keep showing stale
          // rows underneath the spinner while the new search is in flight
          this.data = [];
          return this.service.searchEntries(this.buildParams());
        })
      ).subscribe({
      next: data => {
        this.isLoadingResults = false;
        this.resultsLength = data.totalElements;
        this.data = data.content;
        if (this.resultsLength < 1 && !this.displayEditingTools) {
          this.setTab.emit(1)
        } else {
          this.setTab.emit(0)
        }
      },
      error: error => {
        this.isLoadingResults = false;
        this.notificationService.openSnackBar(error);
      }
    });
  }

  /**
   * trigger a search via the shared switchMap pipeline in ngAfterViewInit, so it cancels
   * whatever request (filter, sort, or page change) was still in flight instead of racing it
   */
  search() {
    this.searchSubject.next();
  }

  private buildParams(): HttpParams {
    let params = this.searchTrigger.params
    if (String(this.sort.active) !== 'created') {
      params = params.append('order', String(this.sort.direction));
      params = params.append('sort', String(this.sort.active));
    }
    params = params.append('size', String(this.paginator.pageSize));
    params = params.append('page', String(this.paginator.pageIndex));
    if (this.selectedCategory != null) {
      params = params.append('categoryId', String(this.selectedCategory));
    }
    return params;
  }


  /**
   * checks if the value is a Country and returns the given string value of Country
   *
   * @param value a Country element
   */
  getCountryDetails(value: Country): string | undefined {
    return this.formOptionsLists.countryToString.get(Country[value]);
  }

  openRegisterPopup() {
    this.notificationService.openSnackBarWithAction('Bitte registrieren Sie sich kostenlos, um Details und Beispiele anzusehen!', 'Registrieren')
      .subscribe(() => {
        this.router.navigateByUrl('/login', {state: {register: true}});
      });
  }

  deleteEntry($event: Word | SystemicGrammarEntryDto | FuncGrammarEntryDto) {
    this.usageText($event.id).subscribe(usageText => this.confirmDelete($event, usageText));
  }

  private confirmDelete($event: Word | SystemicGrammarEntryDto | FuncGrammarEntryDto, usageText: string) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        headline: 'Eintrag löschen',
        content: 'Der Eintrag "' + $event.name + '" und alle dazugehörigen Daten werden gelöscht und können unter keinen Umständen ' +
          'wiederhergestellt werden.' + usageText + ' \n  Wollen Sie den Eintrag wirklich löschen?',
        action: 'Löschen'
      }
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.service.deleteEntry($event.id).subscribe(() => {
          this.selectedIds.delete($event.id);
          this.getRoots.emit();
          this.search();
          this.notificationService.openSnackBar('Eintrag ' + $event.name + ' wurde gelöscht.');
        }, error => {
          this.notificationService.openSnackBar(error);
        });
      }
    });
  }

  editEntry($event: number) {
    let component;
    if (this.service instanceof SysGrammarService) {
      component = NewGrammarEntryComponent;
    } else if (this.service instanceof FuncGrammarService) {
      component = NewGrammarEntryComponent;
    } else if (this.service instanceof WordService) {
      component = NewWordComponent;
    } else if (this.service instanceof TextTypeService) {
      component = NewTextTypeComponent;
    } else if (this.service instanceof VerbalActionEntryService) {
      component = NewVerbalActionComponent;
    } else if (this.service instanceof GlobalCanDoStatementService || this.service instanceof DetailedCanDoStatementService) {
      component = NewCanDoStatementComponent;
    }
    const dialogRef = this.dialog.open(component, {
      data: {
        id: $event,
        service: this.service
      }
    });
    dialogRef.afterClosed().subscribe({
      next: result => {
        if (result) {
          this.getRoots.emit();
          this.search();
        }
      }, error: (error) => {
        this.notificationService.openSnackBar(error);
      }
    });
  }

  showModes(modes: string[]): string {
    if (!modes) return '';
    return this.sharedService.show(
      modes.map(e => this.formOptionsLists.modesToString.get(e))
    );
  }

  showDomains(domains: string[]): string {
    if (!domains) return '';
    return this.sharedService.show(
      domains.map(d => this.formOptionsLists.domainsToString.get(d))
    );
  }

  showInteractions(interactions: string[]): string {
    if (!interactions) return '';
    return this.sharedService.show(
      interactions.map(r => this.formOptionsLists.interactionsToString.get(r))
    );
  }

}
